// The brand mark's outline, a right footprint: 42.4 wide, 100 long, toes up, big toe on the left.
const MARK = new Path2D('M7.9 0l-2.1 .4l-1.8 1.3l-1.4 2l-.9 2.6l-.1 .8l.9 1.1l3.5 1.9l-3.3 .9l-.8 .8l.7 3.3l1.9 3.3l1.9-.1l.1-1.5l.6-.5l2.9 .7l3.2-.7l2.5-1.9l1.2-2.3l-.1-2.9l-1.6-2.6l-.2-1.6l-1.6-2.8l-2-2zM22.7 4.2l-2.4 1l-2.5 2l.5 1.9l.3 3.5l7-2.2l1-1.2l.6-2.3l-.6-1.7l-1.6-.9zM29.8 8.4l-2.1 1.1l-.5 2l.7 .1l.1 1.5l3.8 .7l1.6-1.5l.4-2l-1.4-1.4zM23.8 12.2l-.8 .2l-.6 .7l.1 .9l.7 .7l.3-1.3l.7-.8zM35.7 12.2l-1.4 .4l-.8 1l-.1 1.4l.5 1.4l.9 .8l4-.1l.6-1.1l-.5-.2l-.1-2.5l-1.1-.9zM21.3 12.4l-1.7 1l.4 1.7l1.6 1.3l2.2 .3l-.4-.9l-1.4-1.3zM28.6 14.6l-.7 .4l.9 1.5l1.9-.6l-.6-1.3zM16.9 16.8l-11.2 4.8l-4 8.4l-1.5 5.3l-.2 2.5l3.2 4.2l3.9 4.2l2.9 0l2.5 1.5l1.4 1.9l.6 2.5l7.9 2.3l-.3 6.7l-1.5 7.5l-3.3 3.6l-7.4 5.9l-.8 1.6l-.4 3l0 4.4l.6 4.7l.8 3.2l1 2.1l1.3 1.6l1.4 .7l5.9 .6l4.3-.6l3-1.9l3.3-4.1l2.2-4.9l1.2-8l2.4-24.9l3.2-12.2l2.6-6.4l-.1-2.8l-.9-4.3l1.1-6l-.9-1l-1.6-.2l-1.2 .5l-.7 1.2l-1-.3l-.8 .3l-2.6-2l-1.1-4.6l-2.5 .2l-2.8 1.7l-7.5-2.6zM38 17.8l-1.1 .6l.2 2.7l1.2 .9l1.6 0l1.9 .5l.5-.7l-.5-1.1l.5-1.8l-1.8-.8z')
const MARK_W = 42.4
const MARK_H = 100

// Gait in foot lengths and steps: see a calm adult walk, slowed down.
const STEP = 2.7
const HALF_WIDTH = 0.17
const TOE_OUT = 0.17
const STEP_MS = 920
const STANCE_MS = STEP_MS * 1.2
const GAP_STEPS = 3
const TRAIL_STEPS = 7
const CURSOR_LIFE_STEPS = 3.5
// Walkers on the panel at once, each on its own line and in the next colour of the panel's palette.
const CROWD = 3
const CROWD_WIDE = 4

// Load order within one stance, as fractions of it: [onset, lift-off].
const REGIONS = [
  [0, 0.5],
  [0.12, 0.6],
  [0.28, 0.85],
  [0.4, 0.95],
  [0.55, 1],
]
const DEPTHS = [[0, 0.55], [0.2, 0.8], [0.45, 1]]

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)')
const finePointer = matchMedia('(hover: hover) and (pointer: fine)')

const smoothstep = (a, b, x) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

function random(seed) {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453
  return x - Math.floor(x)
}

/** How much of the mark at (x, y) belongs to each region: heel, outer midfoot, outer ball, inner ball, toes. */
function regionWeights(x, y) {
  const toes = 1 - smoothstep(17, 26, y)
  const heel = smoothstep(64, 76, y)
  const ball = (1 - toes) * (1 - smoothstep(42, 52, y))
  const outer = smoothstep(14, 28, x)
  return [heel, Math.max(0, 1 - toes - ball - heel), ball * outer, ball * (1 - outer), toes]
}

function blur(values, width, height, radius) {
  const pass = (source, count, lines, along, across) => {
    const out = new Float32Array(source.length)
    for (let line = 0; line < lines; line++) {
      for (let i = 0; i < count; i++) {
        let sum = 0
        for (let k = -radius; k <= radius; k++) sum += source[line * across + Math.min(count - 1, Math.max(0, i + k)) * along]
        out[line * across + i * along] = sum / (radius * 2 + 1)
      }
    }
    return out
  }
  return pass(pass(values, width, height, 1, width), height, width, width, 1)
}

/** A smooth line through `points` (Catmull-Rom), sampled as [x, y, length so far]. */
function spline(points) {
  const line = [[points[0][0], points[0][1], 0]]
  for (let i = 0; i < points.length - 1; i++) {
    const [p0, p1, p2, p3] = [points[Math.max(0, i - 1)], points[i], points[i + 1], points[Math.min(points.length - 1, i + 2)]]
    for (let s = 1; s <= 24; s++) {
      const t = s / 24
      const at = k =>
        0.5 * (2 * p1[k] + (p2[k] - p0[k]) * t + (2 * p0[k] - 5 * p1[k] + 4 * p2[k] - p3[k]) * t * t + (3 * p1[k] - p0[k] - 3 * p2[k] + p3[k]) * t ** 3)
      const [px, py, length] = line[line.length - 1]
      line.push([at(0), at(1), length + Math.hypot(at(0) - px, at(1) - py)])
    }
  }
  return line
}

/**
 * A walker's line of travel, from outside the panel back to outside it. The route varies per
 * walker: straight across (behind the copy, where prints dim), a wander between random spots,
 * corner to corner, up the open ground beside the copy (`sides`, as [left, right] spans), a loop
 * around the copy (`copy`, its bounding box) or in from an edge, a turn and back out.
 */
function route(seed, width, height, margin, sides, copy) {
  const r = k => random(seed + k * 13)
  const between = (k, from, to) => from + r(k) * (to - from)
  const flipX = points => points.map(([x, y]) => [width - x, y])
  const flipY = points => points.map(([x, y]) => [x, height - y])
  const offEdge = (k, edge) =>
    [[-margin, between(k, 0.15, 0.85) * height], [width + margin, between(k, 0.15, 0.85) * height], [between(k, 0.1, 0.9) * width, -margin], [between(k, 0.1, 0.9) * width, height + margin]][edge]
  const turn = Math.max(margin * 1.3, height * 0.14)
  const ring = copy && {
    x: (copy.left + copy.right) / 2,
    y: (copy.top + copy.bottom) / 2,
    rx: (copy.right - copy.left) / 2 + margin,
    ry: Math.min((copy.bottom - copy.top) / 2 + margin * 0.6, (copy.top + copy.bottom) / 2 - margin * 0.4),
  }
  const routes = [
    [2, () => [offEdge(1, 0), [width / 3, between(2, 0.1, 0.9) * height], [(width * 2) / 3, between(3, 0.1, 0.9) * height], offEdge(4, 1)]],
    [2, () => {
      const from = Math.floor(r(1) * 4)
      const to = (from + 1 + Math.floor(r(2) * 3)) % 4
      const spots = [3, 4, 5].map(k => [between(k, 0.1, 0.9) * width, between(k + 10, 0.12, 0.88) * height])
      return [offEdge(6, from), ...spots, offEdge(7, to)]
    }],
    [1, () => [[-margin, height + margin], [between(1, 0.3, 0.45) * width, between(2, 0.45, 0.75) * height], [between(3, 0.55, 0.7) * width, between(4, 0.25, 0.55) * height], [width + margin, -margin]]],
    [sides.length ? 1.5 : 0, () => {
      const [left, right] = sides[Math.floor(r(1) * sides.length)]
      const at = (k, from, to) => left + between(k, from, to) * (right - left)
      return [[at(2, 0.15, 0.4), height + margin], [at(3, 0.25, 0.75), height / 2], [at(4, 0.6, 0.85), -margin]]
    }],
    [ring && ring.x - ring.rx > margin * 0.5 && ring.ry > margin ? 1.5 : 0, () => {
      const arc = [0, 1, 2, 3, 4, 5, 6].map(i => Math.PI - (i * Math.PI) / 6).map(a => [ring.x + ring.rx * Math.cos(a), ring.y - ring.ry * Math.sin(a)])
      return [[ring.x - ring.rx, height + margin], ...arc, [ring.x + ring.rx, height + margin]]
    }],
    [1, () => {
      const y = between(1, 0.2, 0.8 - turn / height) * height
      const depth = between(2, 0.18, 0.38) * width
      return [[-margin, y], [depth - turn, y], [depth, y + turn], [depth - turn, y + turn * 2], [-margin, y + turn * 2]]
    }],
  ]
  let pick = r(0) * routes.reduce((sum, [weight]) => sum + weight, 0)
  let points = routes.find(([weight]) => (pick -= weight) < 0)[1]()
  if (r(8) < 0.5) points = flipX(points)
  if (r(9) < 0.5) points = flipY(points)
  if (r(11) < 0.5) points.reverse()
  return spline(points)
}

function pointAt(line, distance) {
  let i = 1
  while (i < line.length - 1 && line[i][2] < distance) i++
  const [x0, y0, d0] = line[i - 1]
  const [x1, y1, d1] = line[i]
  const t = (distance - d0) / (d1 - d0 || 1)
  return { x: x0 + (x1 - x0) * t, y: y0 + (y1 - y0) * t, heading: Math.atan2(y1 - y0, x1 - x0) }
}

class CampaignHeroFootsteps extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('canvas')
    this.context = this.canvas.getContext('2d')
    this.panel = this.closest('.campaign-hero') ?? this.parentElement
    this.elapsed = 0
    this.visible = false

    this.onFrame = () => this.frame()
    this.onVisibility = () => this.updateRunning()
    this.onPointerMove = event => this.trackPointer(event)
    this.onPointerLeave = () => { this.pointer = null }
    this.onPreference = () => this.layout()

    this.resizeObserver = new ResizeObserver(() => this.layout())
    this.resizeObserver.observe(this.panel)
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting
      this.updateRunning()
    })
    this.intersectionObserver.observe(this)
    document.addEventListener('visibilitychange', this.onVisibility)
    reducedMotion.addEventListener('change', this.onPreference)
    this.panel.addEventListener('pointermove', this.onPointerMove, { passive: true })
    this.panel.addEventListener('pointerleave', this.onPointerLeave)
    document.fonts?.ready.then(() => this.layout())
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect()
    this.intersectionObserver.disconnect()
    document.removeEventListener('visibilitychange', this.onVisibility)
    reducedMotion.removeEventListener('change', this.onPreference)
    this.panel.removeEventListener('pointermove', this.onPointerMove)
    this.panel.removeEventListener('pointerleave', this.onPointerLeave)
    this.ratioQuery?.removeEventListener('change', this.onPreference)
    this.pause()
  }

  get running() {
    return this.startedAt !== undefined
  }

  now() {
    return this.running ? this.elapsed + performance.now() - this.startedAt : this.elapsed
  }

  updateRunning() {
    const shouldRun = this.visible && !document.hidden && !reducedMotion.matches && this.width > 0
    if (shouldRun && !this.running) {
      this.startedAt = performance.now()
      this.frame()
    } else if (!shouldRun && this.running) {
      this.elapsed = this.now()
      this.pause()
      this.startedAt = undefined
    }
  }

  pause() {
    cancelAnimationFrame(this.frameRequest)
    clearTimeout(this.wakeTimer)
    this.frameRequest = this.wakeTimer = undefined
  }

  wake() {
    if (this.running && this.frameRequest === undefined) {
      clearTimeout(this.wakeTimer)
      this.wakeTimer = undefined
      this.frameRequest = requestAnimationFrame(this.onFrame)
    }
  }

  layout() {
    const rect = this.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    this.width = rect.width
    this.height = rect.height
    this.ratio = Math.min(devicePixelRatio || 1, 2)
    this.ratioQuery?.removeEventListener('change', this.onPreference)
    this.ratioQuery = matchMedia(`(resolution: ${devicePixelRatio}dppx)`)
    this.ratioQuery.addEventListener('change', this.onPreference)
    this.canvas.width = Math.round(rect.width * this.ratio)
    this.canvas.height = Math.round(rect.height * this.ratio)

    this.copy = [...this.panel.children]
      .filter(child => !child.contains(this))
      .map(child => {
        const r = child.getBoundingClientRect()
        const large = parseFloat(getComputedStyle(child).fontSize) > 24
        return { left: r.left - rect.left, top: r.top - rect.top, right: r.right - rect.left, bottom: r.bottom - rect.top, dim: large ? 0.45 : 0.25 }
      })

    this.footLength = Math.min(rect.width >= 1000 ? 42 : 36, Math.max(28, Math.min(rect.height * 0.145, rect.width * 0.095)))
    const copyLeft = Math.min(...this.copy.map(box => box.left))
    const copyRight = Math.max(...this.copy.map(box => box.right))
    this.sides = !this.copy.length ? [] : [[0, copyLeft], [copyRight, rect.width]].filter(([from, to]) => to - from > this.footLength * 3)
    this.copyBox = this.copy.length && { left: copyLeft, right: copyRight, top: Math.min(...this.copy.map(box => box.top)), bottom: Math.max(...this.copy.map(box => box.bottom)) }
    this.sprites = this.drawSprites()
    this.passes = []
    this.walkers = 0
    this.cursorPrints = []
    const first = Math.ceil(this.now() / STEP_MS)
    this.lanes = Array.from({ length: rect.width >= 1000 ? CROWD_WIDE : CROWD }, (_, lane) => ({ nextPass: first + lane * 4 }))
    this.wake()
    this.updateRunning()
    if (!this.running) this.draw(...(reducedMotion.matches ? this.still() : [this.now()]))
  }

  /** A walker crossing the panel from step `start`: one print per step along a fresh line of travel. */
  walk(start) {
    const seed = ++this.walkers * 7
    const ink = this.walkers % this.sprites.inks.length
    const length = this.footLength
    const line = route(seed, this.width, this.height, length * 1.5, this.sides, this.copyBox)
    const prints = []
    const first = random(seed + 4) < 0.5 ? 1 : -1
    for (let k = 0, along = length * 0.5; along < line[line.length - 1][2]; k++) {
      const { x, y, heading } = pointAt(line, along)
      const side = k % 2 ? -first : first
      const angle = heading + side * (TOE_OUT + (random(seed + k * 3) - 0.5) * 0.08)
      const offset = side * HALF_WIDTH * length
      prints.push(this.print(x - Math.sin(heading) * offset, y + Math.cos(heading) * offset, angle, side, (start + k) * STEP_MS, undefined, ink))
      along += STEP * length * (1 + (random(seed + k * 3 + 1) - 0.5) * 0.08)
    }
    const visible = prints.filter(print => this.onPanel(print))
    const lifeSteps = Math.min(TRAIL_STEPS, visible.length) + 0.6
    for (const print of prints) print.lifeSteps = lifeSteps
    const lastVisible = start + prints.indexOf(visible[visible.length - 1])
    return { start, end: start + prints.length, prints, gone: lastVisible + lifeSteps }
  }

  onPanel(print) {
    const reach = this.footLength
    return print.x > -reach && print.x < this.width + reach && print.y > -reach && print.y < this.height + reach
  }

  print(x, y, heading, side, landsAt, lifeSteps, ink = 0) {
    return { x, y, side, landsAt, lifeSteps, ink, angle: heading + Math.PI / 2, dim: this.dimAt(x, y) }
  }

  dimAt(x, y) {
    const [overlaps, clear] = [this.footLength * 0.55, this.footLength * 1.15]
    return this.copy.reduce((dim, box) => {
      const dx = Math.max(box.left - x, 0, x - box.right)
      const dy = Math.max(box.top - y, 0, y - box.bottom)
      return Math.min(dim, box.dim + (1 - box.dim) * smoothstep(overlaps, clear, Math.hypot(dx, dy)))
    }, 1)
  }

  passesAt(time) {
    const step = time / STEP_MS
    for (const lane of this.lanes) {
      while (lane.nextPass <= step + GAP_STEPS) {
        const pass = this.walk(lane.nextPass)
        this.passes.push(pass)
        lane.nextPass = pass.end + 1 + Math.floor(random(this.walkers * 7 + 5) * GAP_STEPS)
      }
    }
    this.passes = this.passes.filter(pass => pass.gone + 1 > step)
    return this.passes
  }

  /** The reduced-motion frame: a walker most of the way across, its trail settled behind it. */
  still() {
    const { prints } = this.passesAt(0)[0]
    const settled = prints.filter(p => this.onPanel(p))
    const newest = settled[Math.max(0, Math.round(settled.length * 0.8) - 1)] ?? prints[0]
    return [newest.landsAt + STANCE_MS + 1, newest.landsAt]
  }

  /**
   * Seven sprites cut from one wet imprint, per ink of the panel's palette: per load region (heel to
   * toes) and per depth (edge to centre), so a print can press in region by region and dry from its
   * edges inward.
   */
  drawSprites() {
    const scale = (this.footLength * this.ratio) / MARK_H
    const pad = Math.ceil(this.footLength * this.ratio * 0.06) + 2
    const width = Math.ceil(MARK_W * scale) + pad * 2
    const height = Math.ceil(MARK_H * scale) + pad * 2
    const canvas = () => Object.assign(document.createElement('canvas'), { width, height })

    const base = canvas().getContext('2d')
    base.fillStyle = '#fff'
    base.setTransform(scale, 0, 0, scale, pad, pad)
    base.fill(MARK)
    const pixels = base.getImageData(0, 0, width, height).data
    const traced = Float32Array.from({ length: width * height }, (_, i) => pixels[i * 4 + 3] / 255)
    const rounded = blur(traced, width, height, Math.max(1, Math.round(this.footLength * this.ratio * 0.03)))
    const alpha = rounded.map(value => smoothstep(0.3, 0.62, value))
    const depth = blur(alpha, width, height, Math.max(1, Math.round(this.footLength * this.ratio * 0.05)))

    const palette = getComputedStyle(this.panel).getPropertyValue('--campaign-hero-inks').trim() || '255 255 255'
    const inks = palette.split(',').map(ink => ink.trim().split(/\s+/).map(Number))
    const layers = inks.map(() => [...REGIONS, ...DEPTHS].map(() => new ImageData(width, height)))
    for (let i = 0; i < alpha.length; i++) {
      if (!alpha[i]) continue
      const regions = regionWeights(((i % width) - pad) / scale, (Math.floor(i / width) - pad) / scale)
      const outer = 1 - smoothstep(0.5, 0.72, depth[i])
      const inner = smoothstep(0.8, 0.95, depth[i])
      const weights = [...regions, outer, 1 - outer - inner, inner]
      layers.forEach((set, n) =>
        set.forEach((layer, k) => {
          layer.data.set(inks[n], i * 4)
          layer.data[i * 4 + 3] = alpha[i] * weights[k] * 255
        }),
      )
    }
    return {
      offset: [pad + (MARK_W / 2) * scale, pad + (MARK_H / 2) * scale],
      inks: layers.map(set => {
        const sprites = set.map(layer => {
          const sprite = canvas()
          sprite.getContext('2d').putImageData(layer, 0, 0)
          return sprite
        })
        return { regions: sprites.slice(0, REGIONS.length), depths: sprites.slice(REGIONS.length) }
      }),
    }
  }

  frame() {
    this.frameRequest = undefined
    const time = this.now()
    const wakeAt = this.draw(time)
    if (wakeAt <= time) this.frameRequest = requestAnimationFrame(this.onFrame)
    else this.wakeTimer = setTimeout(this.onFrame, wakeAt - time)
  }

  /** Paints the moment `time`, leaving out prints landing after `lastLanding`; returns when the picture next changes. */
  draw(time, lastLanding = Infinity) {
    const { context } = this
    context.setTransform(1, 0, 0, 1, 0, 0)
    context.clearRect(0, 0, this.canvas.width, this.canvas.height)
    context.globalCompositeOperation = 'lighter'
    let wakeAt = Infinity
    const prints = [...this.passesAt(time).flatMap(pass => pass.prints), ...this.cursorPrints]
    for (const print of prints) {
      if (!this.onPanel(print) || print.landsAt > lastLanding) continue
      if (print.landsAt > time) wakeAt = Math.min(wakeAt, print.landsAt)
      else if (this.stamp(print, time)) wakeAt = time
    }
    this.cursorPrints = this.cursorPrints.filter(print => time - print.landsAt < print.lifeSteps * STEP_MS)
    return Math.min(wakeAt, time + STEP_MS * GAP_STEPS)
  }

  /** Draws one print at `time`; returns whether it is still changing. */
  stamp(print, time) {
    const age = (time - print.landsAt) / STEP_MS
    if (age >= print.lifeSteps) return false
    const { context, sprites, ratio } = this
    const cos = Math.cos(print.angle)
    const sin = Math.sin(print.angle)
    context.setTransform(cos * print.side, sin * print.side, -sin, cos, print.x * ratio, print.y * ratio)
    const [ox, oy] = sprites.offset
    const { regions, depths } = sprites.inks[print.ink]

    const wet = (0.39 + 0.51 * Math.exp(-age / 5)) * print.dim
    const drySteps = Math.min(print.lifeSteps, 2.4)
    const dried = smoothstep(print.lifeSteps - drySteps, print.lifeSteps, age)
    const stance = (time - print.landsAt) / STANCE_MS
    if (stance < 1) {
      const remaining = 1 - dried
      REGIONS.forEach(([on, off], index) => {
        const pressed = smoothstep(on, on + 0.08, stance) * (1 - smoothstep(off - 0.12, off, stance))
        context.globalAlpha = Math.min(1, wet * remaining * smoothstep(on, on + 0.12, stance) + 0.9 * pressed * print.dim)
        context.drawImage(regions[index], -ox, -oy)
      })
    } else {
      DEPTHS.forEach(([from, to], index) => {
        context.globalAlpha = wet * (1 - smoothstep(from, to, dried))
        context.drawImage(depths[index], -ox, -oy)
      })
    }
    context.globalAlpha = 1
    return true
  }

  trackPointer(event) {
    if (event.pointerType !== 'mouse' || !finePointer.matches || reducedMotion.matches || !this.sprites) return
    const rect = this.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    if (!this.pointer) {
      this.pointer = { x, y, side: 1 }
      return
    }
    const dx = x - this.pointer.x
    const dy = y - this.pointer.y
    if (Math.hypot(dx, dy) < STEP * this.footLength) return
    const heading = Math.atan2(dy, dx)
    const side = -this.pointer.side
    const offset = side * HALF_WIDTH * this.footLength
    this.cursorPrints.push(this.print(x - Math.sin(heading) * offset, y + Math.cos(heading) * offset, heading + side * TOE_OUT, side, this.now(), CURSOR_LIFE_STEPS))
    if (this.cursorPrints.length > 8) this.cursorPrints.shift()
    this.pointer = { x, y, side }
    this.wake()
  }
}

if (!customElements.get('campaign-hero-footsteps')) customElements.define('campaign-hero-footsteps', CampaignHeroFootsteps)
